// client/src/hooks/useTestimonials.js
import { useState, useCallback } from 'react';
import api from '../services/api';
import useAlert from './useAlert';

export const useTestimonials = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setAlert } = useAlert();

    // --- DEPOIMENTOS PÚBLICOS (Visíveis no site institucional sem login) ---
    const fetchPublicTestimonials = useCallback(async (filters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const cleanFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) =>
                    value !== '' && value !== null && value !== undefined
                )
            );

            const params = new URLSearchParams(cleanFilters).toString();
            const response = await api.get(`/institucional/testimonials?${params}`);
            setTestimonials(response.data.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao buscar depoimentos públicos';
            setError(msg);
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    // --- MEUS DEPOIMENTOS (Depoimentos CRIADOS pelo usuário logado) ---
    const fetchMyTestimonials = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/admin/institucional/my-testimonials');
            setTestimonials(response.data.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao buscar seus depoimentos';
            setError(msg);
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const createUserTestimonial = useCallback(async (data) => {
        try {
            // ✅ CORREÇÃO: Nova URL sem conflito
            const response = await api.post('/admin/institucional/my-testimonials', data);
            setAlert('Depoimento enviado para revisão com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao enviar depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // Criar depoimento SOBRE A PLATAFORMA (realizado pelo usuário logado)
    const createPlatformTestimonial = useCallback(async (data) => {
        try {
            // ✅ CORREÇÃO: Nova URL sem conflito
            const response = await api.post('/admin/institucional/platform-testimonials', {
                ...data,
                type: 'platform'
            });
            setAlert('Depoimento sobre a plataforma enviado para revisão!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao enviar depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // Criar depoimento SOBRE A CONTA (realizado pelo usuário logado)
    const createAccountTestimonial = useCallback(async (data) => {
        try {
            // ✅ CORREÇÃO: Nova URL sem conflito
            const response = await api.post('/admin/institucional/account-testimonials', {
                ...data,
                type: 'account'
            });
            setAlert('Depoimento sobre a conta enviado para revisão!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao enviar depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const updateMyTestimonial = useCallback(async (id, data) => {
        try {
            // ✅ CORREÇÃO: Nova URL sem conflito
            const response = await api.put(`/admin/institucional/my-testimonials/${id}`, data);
            setAlert('Depoimento atualizado e reenviado para revisão!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar seu depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const deleteMyTestimonial = useCallback(async (id) => {
        try {
            // ✅ CORREÇÃO: Nova URL sem conflito
            await api.delete(`/admin/institucional/my-testimonials/${id}`);
            setTestimonials(prev => prev.filter(t => t._id !== id));
            setAlert('Seu depoimento foi excluído com sucesso!', 'success');
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao excluir seu depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // --- DEPOIMENTOS DA CONTA (Depoimentos RECEBIDOS por clientes da conta do usuário logado) ---
    const fetchAccountTestimonials = useCallback(async (filters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const cleanFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) =>
                    value !== '' && value !== null && value !== undefined
                )
            );

            const params = new URLSearchParams(cleanFilters).toString();
            const response = await api.get(`/admin/institucional/account-testimonials?${params}`);
            setTestimonials(response.data.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao buscar depoimentos da conta';
            setError(msg);
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    // --- DEPOIMENTOS DOS USUÁRIOS DA CONTA ---
    const fetchAccountUsersTestimonials = useCallback(async (filters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const cleanFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) =>
                    value !== '' && value !== null && value !== undefined
                )
            );

            const params = new URLSearchParams(cleanFilters).toString();
            const response = await api.get(`/admin/institucional/account-users-testimonials?${params}`);
            setTestimonials(response.data.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao buscar depoimentos dos usuários da conta';
            setError(msg);
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const updateAccountTestimonialStatus = useCallback(async (id, status) => {
        try {
            // ✅ CORREÇÃO: Nova URL sem conflito
            const response = await api.put(`/admin/institucional/account-testimonials/${id}`, { status });
            setTestimonials(prev => prev.map(t => (t._id === id ? { ...t, status } : t)));
            setAlert('Status do depoimento atualizado com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar status do depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const deleteAccountTestimonial = useCallback(async (id) => {
        try {
            // ✅ CORREÇÃO: Nova URL sem conflito
            await api.delete(`/admin/institucional/account-testimonials/${id}`);
            setTestimonials(prev => prev.filter(t => t._id !== id));
            setAlert('Depoimento excluído com sucesso!', 'success');
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao excluir depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // --- DEPOIMENTOS PLATAFORMA (Admin Matriz) ---
    const fetchPlatformTestimonials = useCallback(async (filters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const cleanFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) =>
                    value !== '' && value !== null && value !== undefined
                )
            );

            const params = new URLSearchParams(cleanFilters).toString();
            const response = await api.get(`/admin/institucional/platform-testimonials?${params}`);
            setTestimonials(response.data.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao buscar depoimentos da plataforma';
            setError(msg);
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    // --- ADMIN MATRIZ (Todos os depoimentos - Acesso global) ---
    const fetchAdminTestimonials = useCallback(async (filters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const cleanFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) =>
                    value !== '' && value !== null && value !== undefined
                )
            );

            const params = new URLSearchParams(cleanFilters).toString();
            const response = await api.get(`/admin/institucional/testimonials?${params}`);
            setTestimonials(response.data.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao buscar depoimentos';
            setError(msg);
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const adminUpdateTestimonial = useCallback(async (id, data) => {
        try {
            // ✅ MANTIDO: Esta rota ainda usa /testimonials/:id (apenas para matriz admin)
            const response = await api.put(`/admin/institucional/testimonials/${id}`, data);
            setAlert('Depoimento atualizado com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const adminDeleteTestimonial = useCallback(async (id) => {
        try {
            // ✅ MANTIDO: Esta rota ainda usa /testimonials/:id (apenas para matriz admin)
            await api.delete(`/admin/institucional/testimonials/${id}`);
            setTestimonials(prev => prev.filter(t => t._id !== id));
            setAlert('Depoimento excluído com sucesso!', 'success');
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao excluir depoimento';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const updateAdminTestimonialsOrder = useCallback(async (order) => {
        try {
            // ✅ MANTIDO: Esta rota ainda usa a mesma URL
            await api.put('/admin/institucional/testimonials/update-order', { order });
            setAlert('Ordem dos depoimentos atualizada com sucesso!', 'success');
            return true;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar ordem dos depoimentos';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    return {
        testimonials,
        loading,
        error,
        // Públicos
        fetchPublicTestimonials,
        // Meus depoimentos (enviados pelo usuário logado)
        fetchMyTestimonials,
        createUserTestimonial,
        createPlatformTestimonial,
        createAccountTestimonial,
        updateMyTestimonial,
        deleteMyTestimonial,
        // Depoimentos da conta (recebidos pela conta do usuário logado)
        fetchAccountTestimonials,
        fetchAccountUsersTestimonials, // ✅ NOVA FUNÇÃO: Depoimentos dos usuários da conta
        updateAccountTestimonialStatus,
        deleteAccountTestimonial,
        // Depoimentos plataforma (gerenciado por Admin Matriz)
        fetchPlatformTestimonials,
        // Admin matriz (todos os depoimentos)
        fetchAdminTestimonials,
        adminUpdateTestimonial,
        adminDeleteTestimonial,
        updateAdminTestimonialsOrder,
    };
};